import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package, Play, Loader2, ArrowLeft, Sparkles, ShoppingCart, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 27c · Video Pack detail (landing) with 3 preview videos + CTA "Get pack".
 * Route: /videos/pack/:pack_id
 * - Public preview (no auth) — hooks pack_preview_view upsell event
 * - Cover image via /api/videos/v2/packs/{pack_id}/cover
 */
export default function VideoPackDetail() {
  const { pack_id } = useParams();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/videos/v2/packs/${encodeURIComponent(pack_id)}/preview`, {
        credentials: "include",
      });
      if (r.status === 404) {
        setError("not_found");
        setLoading(false);
        return;
      }
      if (r.status === 503) {
        setError("pack_v2_disabled");
        setLoading(false);
        return;
      }
      if (!r.ok) throw new Error(`http_${r.status}`);
      const data = await r.json();
      setPack(data);
      // Fire pack preview view — upsell wire (non-blocking, guest-safe)
      fetch(`${API}/api/upsell/context/pack-preview-view`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack_id, surface: "video_pack" }),
      }).catch(() => {});
    } catch (e) {
      setError(e?.message || "load_error");
    } finally {
      setLoading(false);
    }
  }, [pack_id]);

  useEffect(() => { load(); }, [load]);

  // SEO — JSON-LD Product schema for the pack
  useEffect(() => {
    if (!pack) return;
    try {
      const el = document.getElementById("pack-jsonld")
        || document.createElement("script");
      el.id = "pack-jsonld";
      el.type = "application/ld+json";
      const priceEUR = ((pack.pack_v2_price_cents || 0) / 100).toFixed(2);
      el.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: pack.title,
        description: pack.description,
        brand: { "@type": "Brand", name: "Global Lead Suite" },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: priceEUR,
          availability: pack.is_monetized
            ? "https://schema.org/InStock"
            : "https://schema.org/PreOrder",
        },
      });
      if (!el.parentNode) document.head.appendChild(el);
      document.title = `${pack.title} — Video Pack`;
    } catch {}
  }, [pack]);

  const goBuy = () => {
    if (pack?.linked_marketplace_asset_id) {
      window.location.href = `/marketplace/asset/${pack.linked_marketplace_asset_id}`;
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
    </div>
  );

  if (error === "pack_v2_disabled") return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)] flex items-center justify-center p-6">
      <div className="text-center max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-8">
        <Package className="h-10 w-10 text-text-secondary mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">Video Packs are paused</h1>
        <p className="text-sm text-text-secondary">The premium pack feature is currently disabled by the operator.</p>
      </div>
    </div>
  );

  if (error === "not_found" || !pack) return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)] flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold mb-2">Pack not found</h1>
        <Link to="/videos" className="text-sm text-[var(--color-primary)] inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to videos
        </Link>
      </div>
    </div>
  );

  const priceEUR = ((pack.pack_v2_price_cents || 0) / 100).toFixed(2);
  const previews = pack.preview_videos || [];

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]" data-testid="video-pack-detail-page">
      <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-8">
        <Link to="/videos" className="inline-flex items-center gap-2 text-xs text-text-secondary hover:text-[var(--color-secondary)]"
          data-testid="pack-detail-back">
          <ArrowLeft className="h-3 w-3" /> All videos
        </Link>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden border border-[var(--color-border)] shadow-2xl relative">
          {pack.cover_url_hint ? (
            <img
              src={`${API}${pack.cover_url_hint}`}
              alt={pack.title}
              className="w-full h-auto"
              data-testid="pack-cover-image"
              style={{ aspectRatio: "16/9", objectFit: "cover" }}
            />
          ) : (
            <div className="w-full flex items-center justify-center bg-white hover:bg-[#EDEDED]"
              style={{ aspectRatio: "16/9" }}>
              <Package className="h-24 w-24 text-white/60" />
            </div>
          )}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#161616]-500/40 text-white border border-[#2A2A2A]-400/60 backdrop-blur">
              <Sparkles className="h-2.5 w-2.5" /> AI Pack
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-black/40 text-white border border-white/30 backdrop-blur">
              <Package className="h-2.5 w-2.5" /> {pack.total_videos_count} videos
            </span>
          </div>
        </motion.div>

        {/* Title + price + CTA */}
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3"
            data-testid="pack-detail-title">
            {pack.title}
          </h1>
          <div className="text-xs text-text-secondary mb-4">
            {pack.total_videos_count} videos · {Math.floor((pack.total_duration_sec || 0) / 60)}min total · Month {pack.month_yyyy_mm}
          </div>
          <p className="text-sm text-text-secondary leading-relaxed max-w-3xl mb-6">
            {pack.description}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-2xl font-semibold text-[var(--color-secondary)]" data-testid="pack-price">
              ${priceEUR}
            </div>
            {pack.is_monetized && pack.linked_marketplace_asset_id ? (
              <button
                data-testid="pack-buy-btn"
                onClick={goBuy}
                className="px-6 py-3 rounded-full bg-white hover:bg-[#EDEDED] text-black text-sm font-semibold inline-flex items-center gap-2 hover:shadow-lg transition-shadow"
              >
                <ShoppingCart className="h-4 w-4" /> Get the full pack
              </button>
            ) : (
              <span className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-neutral-500/20 text-neutral-300 border border-neutral-500/40"
                data-testid="pack-preorder-badge">
                Coming soon
              </span>
            )}
          </div>
        </div>

        {/* Preview videos */}
        {previews.length > 0 && (
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
            data-testid="pack-preview-section">
            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-4">
              Free preview — {previews.length} of {pack.total_videos_count} videos
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {previews.map((v) => (
                <Link key={v.video_id} to={`/video/${v.video_id}`}
                  data-testid={`pack-preview-${v.video_id}`}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] overflow-hidden hover:border-[var(--color-primary)] transition-colors">
                  <div className="aspect-video bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 flex items-center justify-center">
                    <Play className="h-8 w-8 text-white/70" fill="currentColor" />
                  </div>
                  <div className="p-3">
                    <div className="text-xs text-text-secondary mb-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {v.duration_sec}s
                    </div>
                    <div className="text-sm line-clamp-2">{v.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Info footer */}
        <div className="text-[10px] text-text-secondary/70 leading-relaxed border-t border-[var(--color-border)] pt-4">
          🤖 <strong>AI-generated pack</strong> — 100% dei ricavi va alla piattaforma per coprire i costi di generazione + rendering + moderazione AI. Nessun creator umano percepisce ricavi da questo pack.
        </div>
      </div>
    </div>
  );
}
